import { Injectable } from '@angular/core';
/**
 * A service for managing local storage with a specific key prefix.
 * It allows setting and getting items in local storage with a specified property in a typed manner.
 *
 * The service must be initialized with a storage key before use using the `init` method.
 *
 * Example usage:
 * ```typescript
 * import { StorageService } from 'path-to-storage.service';
 *
 * @Component({
 *   provider: [StorageService],
 * })
 * export class MyComponent {
 *   private storageService = inject(StorageService<{filter: string}>>);
 *
 *   private filter = signal('');
 *
 *   constructor() {
 *     this.storageService.init('my-component'); // if the custom key does not start with 'tt-', it will be prefixed with 'tt-'
 *
 *     this.filter.set(this.storageService.getItem('filter') || '');
 *
 *     effect(() => {
 *       this.storageService.setItem('filter', { filter: this.filter() });
 *     });
 *   }
 * }
 * ```
 */

@Injectable()
export class StorageService<T> {
  private initialized = false;

  private storageKey = 'tt';

  public init(storageKey: string) {
    if (!storageKey.startsWith('tt-')) {
      storageKey = `tt-${storageKey}`;
    }
    this.storageKey = storageKey;

    this.initialized = true;
  }

  /**
   * Sets an item in local storage under the specified property.
   * @param property The property key under which the value will be stored.
   * @param value The value to store.
   * @param ttl Time to live in hours.
   */
  public setItem(property: keyof T, value: T, ttl = 24): void {
    if (!this.initialized) {
      console.error(`StorageService is not initialized. Call init() before using setItem for property "${String(property)}".`);
      return;
    }

    if (!value) {
      localStorage.removeItem(`${this.storageKey}.${String(property)}`);
      return;
    }

    const currentValue = this.getItem(property);
    const valueToSave = {
      ...currentValue,
      ...value,
      expiry: new Date().getTime() + ttl * 60 * 60 * 1000,
    };

    localStorage.setItem(`${this.storageKey}.${String(property)}`, JSON.stringify(valueToSave));
  }

  public getItem<U extends keyof T>(property: U): T {
    if (!this.initialized) {
      console.error(`StorageService is not initialized. Call init() before using getItem for property "${String(property)}".`);
      return;
    }

    const item = localStorage.getItem(`${this.storageKey}.${String(property)}`);

    if (item) {
      const parsedItem = JSON.parse(item);
      if (parsedItem.expiry && new Date().getTime() > parsedItem.expiry) {
        localStorage.removeItem(`${this.storageKey}.${String(property)}`);
        return null;
      }
      return parsedItem;
    }

    return null;
  }
}
